import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  YSVLIVE_TOKEN_KEY,
  ysvAuthApi,
} from "@/lib/ysvAuthApi";
import { viewerApi } from "@/lib/api";
import { hasYsvLiveAccess } from "@/lib/viewerAccess";

const ViewerAuthContext = createContext(null);

export function ViewerAuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=signed out
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [freeAccess, setFreeAccess] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);

  useEffect(() => {
    viewerApi
      .get("/config")
      .then(({ data }) => setFreeAccess(!!data.free_access))
      .catch(() => setFreeAccess(false))
      .finally(() => setConfigLoading(false));
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem(YSVLIVE_TOKEN_KEY);
    setUser(false);
    setSubscription(null);
  }, []);

  const loadSession = useCallback(async () => {
    if (!localStorage.getItem(YSVLIVE_TOKEN_KEY)) {
      clearSession();
      setLoading(false);
      return;
    }

    try {
      const [{ data: currentUser }, { data: currentSubscription }] =
        await Promise.all([
          ysvAuthApi.get("/auth/me"),
          ysvAuthApi.get("/subscription/status"),
        ]);
      setUser(currentUser);
      setSubscription(currentSubscription);
    } catch {
      clearSession();
    } finally {
      setLoading(false);
    }
  }, [clearSession]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  useEffect(() => {
    const sessionInvalid = () => clearSession();
    const accessChanged = () => loadSession();
    window.addEventListener("ysvlive:session-invalid", sessionInvalid);
    window.addEventListener("ysvlive:access-changed", accessChanged);
    return () => {
      window.removeEventListener("ysvlive:session-invalid", sessionInvalid);
      window.removeEventListener("ysvlive:access-changed", accessChanged);
    };
  }, [clearSession, loadSession]);

  const login = async (email, password) => {
    const { data } = await ysvAuthApi.post("/auth/login", {
      email,
      password,
      app_id: "ysvlive",
    });
    localStorage.setItem(YSVLIVE_TOKEN_KEY, data.token);

    try {
      const { data: currentSubscription } = await ysvAuthApi.get(
        "/subscription/status",
      );
      setUser(data.user);
      setSubscription(currentSubscription);
      return {
        user: data.user,
        canWatch: hasYsvLiveAccess(currentSubscription),
      };
    } catch (error) {
      clearSession();
      throw error;
    }
  };

  const logout = async () => {
    try {
      await ysvAuthApi.post("/auth/logout");
    } catch {
      // A locally cleared token is still the correct signed-out state when the
      // network is unavailable or the server already invalidated the session.
    }
    clearSession();
  };

  return (
    <ViewerAuthContext.Provider
      value={{
        user,
        subscription,
        loading,
        freeAccess,
        configLoading,
        canWatch: freeAccess || hasYsvLiveAccess(subscription),
        login,
        logout,
        refresh: loadSession,
      }}
    >
      {children}
    </ViewerAuthContext.Provider>
  );
}

export const useViewerAuth = () => useContext(ViewerAuthContext);
